// MoonPay ↔ Ledger Dedupe.
//
// Hintergrund: MoonPay-Käufe und der zugehörige Ledger-IN sind dieselbe Echtwelt-
// Transaktion mit unterschiedlichen Hashes (MoonPay UUID vs. On-Chain TX-Hash).
// Ohne Dedupe würde der Bestand verdoppelt: einmal aus MoonPay als `kauf` (mit
// Anschaffungskosten) und einmal aus Ledger als Wallet-Eingang (ohne Kostenbasis).
//
// Strategie: Wenn ein MoonPay-Kauf existiert, ist der dazugehörige Ledger-Eingang
// redundant — der `kauf` enthält bereits Coin, Menge und korrekte Kostenbasis.
// Match-Kriterien: gleicher Coin, Menge ±0.1 %, Zeit ±30 min.
//
// Wird in zwei Konfigurationen genutzt:
//   1. ImportReviewScreen: neue Ledger-Eingänge gegen bestehende MoonPay-Käufe
//      → Default-Skip im Review.
//   2. useTransactions.addBatch: post-hoc Scrub auf dem gesamten Portfolio
//      → automatisches Aufräumen, egal in welcher Reihenfolge die CSVs kamen.

// Match-Toleranz: MoonPay schickt exakt die gekaufte Menge — kein Spielraum nötig.
// 0.1 % deckt Float-Rundungen ab (z.B. 511 vs. 511.0000001).
const AMOUNT_REL_TOLERANCE = 0.001;
// Absolute Mindesttoleranz für sehr kleine Beträge (Sub-Cent-Coin-Mengen).
const AMOUNT_ABS_TOLERANCE = 1e-6;
// Zeitfenster: MoonPay-Order → On-Chain-Settlement dauert meist <1 min, in
// Ausnahmefällen (verstopfte Chain) bis 30 min.
const TIME_WINDOW_MS = 30 * 60 * 1000;

function txTimestampMs(tx) {
  if (tx.ts) {
    const ms = Date.parse(tx.ts);
    if (!Number.isNaN(ms)) return ms;
  }
  if (tx.datum && tx.zeit) return new Date(`${tx.datum}T${tx.zeit}Z`).getTime();
  if (tx.datum) return new Date(`${tx.datum}T00:00:00Z`).getTime();
  return NaN;
}

function symOf(tx) {
  return String(tx.coin || tx.sym || '').toUpperCase();
}

function amountsMatch(a, b) {
  if (!(a > 0) || !(b > 0)) return false;
  const diff = Math.abs(a - b);
  if (diff <= AMOUNT_ABS_TOLERANCE) return true;
  const rel = diff / Math.max(a, b);
  return rel <= AMOUNT_REL_TOLERANCE;
}

function isMoonPayKauf(tx) {
  return tx && tx.typ === 'kauf' && String(tx.exchange || '').toLowerCase() === 'moonpay';
}

// Set bekannter Eigen-Wallet-Labels. Bei diesen exchange-Werten ist ein EINGANG
// kein Kauf an einer Börse, sondern Wallet-Empfang vom User selbst (typisch:
// MoonPay-Kauf liefert in Phantom/Ledger). Erweiterbar wenn neue Wallets dazukommen.
const KNOWN_WALLET_LABELS = new Set([
  'ledger', 'phantom', 'backpack', 'solflare', 'metamask',
  'trust wallet', 'trustwallet', 'exodus', 'rabby',
]);

// Quellen, die wir als "automatisiert importiert" einstufen — bei diesen ist
// preis>0 NICHT durch User-Edit entstanden (sondern z.B. durch unseren CoinGecko-
// Backfill) und disqualifiziert den Eintrag deshalb nicht als Pair-Kandidat.
const AUTO_IMPORT_SOURCES = new Set([
  'csv_import', 'exchange_api', 'image_import', 'migrated_from_local',
]);

// On-Chain-Wallet-Importe (Adresse/xpub). Ein EINGANG aus so einer Quelle IST ein
// Wallet-Empfang — unabhängig vom exchange-Label (das ist hier oft der Netz-/Coin-Name
// wie "ETH", nicht "Ledger"). Diese Eingänge dürfen deshalb nicht am KNOWN_WALLET_LABELS-
// Filter scheitern, und ihr preis>0 stammt aus unserem Backfill (kein User-Edit).
const CHAIN_IMPORT_SOURCES = new Set([
  'alchemy', 'helius', 'blockchair', 'horizon', 'rippled', 'koios', 'chain_import',
]);
function isOnChainWalletImport(tx) {
  return !!tx && (CHAIN_IMPORT_SOURCES.has(tx.source) || tx.blockchainVerified === true);
}

// Ein "Wallet-Eingang" (vorher: nur Ledger) ist:
//   - exchange ∈ KNOWN_WALLET_LABELS
//   - typ ∈ {einzahlung, kauf} — beides kommt in der Praxis vor (alter AI-Parser
//     hat IN auch mal als kauf klassifiziert)
//   - preis === 0 ODER source ∈ AUTO_IMPORT_SOURCES — schützt User-Edits davor,
//     versehentlich als Duplikat erkannt und gelöscht zu werden, lässt aber
//     automatisch geprict te Importe (CoinGecko-Backfill) weiterhin durch.
function isWalletIn(tx) {
  if (!tx) return false;
  if (tx.typ !== 'einzahlung' && tx.typ !== 'kauf') return false;
  const ex = String(tx.exchange || '').toLowerCase().trim();
  const onChain = isOnChainWalletImport(tx);
  // Wallet-Eingang = bekanntes Wallet-Label ODER On-Chain-Import (Label egal).
  if (!KNOWN_WALLET_LABELS.has(ex) && !onChain) return false;
  if (!(Number(tx.preis) > 0)) return true;            // unberuehrter Eintrag
  if (AUTO_IMPORT_SOURCES.has(tx.source) || onChain) return true; // auto-gepricter Import
  return false;
}

// Backwards-Compat-Alias — interne Funktion, die früher nur Ledger akzeptierte.
const isLedgerIn = isWalletIn;

/**
 * Prüft, ob ein Ledger-Einzahlungs-TX redundant zu einem MoonPay-Kauf ist.
 * @param {object} ledgerIn  Ledger-`einzahlung`-TX
 * @param {object} moonpayKauf  MoonPay-`kauf`-TX
 * @returns {boolean}
 */
export function isMoonPayLedgerDuplicate(ledgerIn, moonpayKauf) {
  if (!isLedgerIn(ledgerIn) || !isMoonPayKauf(moonpayKauf)) return false;
  if (symOf(ledgerIn) !== symOf(moonpayKauf)) return false;
  if (!amountsMatch(Number(ledgerIn.menge), Number(moonpayKauf.menge))) return false;
  const tA = txTimestampMs(ledgerIn);
  const tB = txTimestampMs(moonpayKauf);
  if (Number.isNaN(tA) || Number.isNaN(tB)) return false;
  return Math.abs(tA - tB) <= TIME_WINDOW_MS;
}

/**
 * Findet alle MoonPay↔Ledger-Paare in einer kombinierten Liste.
 * Reihenfolge-unabhängig: egal ob Ledger neu/existing oder MoonPay neu/existing,
 * alle Paare werden zurückgegeben. Verbraucher entscheidet selbst, welche Seite
 * gelöscht/angereichert/gepatcht wird.
 *
 * @param {Array} allTxs  alle bekannten TX (existing + neue)
 * @returns {Array<{ledger: object, moonpay: object}>}  Paare mit Referenzen
 *          auf beide Original-Objekte (für _docId / _editId / etc. Zugriff)
 */
// ⚠️ TRANSFER-SCHUTZ (geteilt, vgl. FLR-Regression): Ein Wallet-Eingang mit einer passenden
// Auszahlung (gleiche Coin+Menge) ZEITNAH (±6 h) ist die Empfangsseite eines TRANSFERs —
// KEINE redundante MoonPay-Lieferung / Swap-Lieferung. Darf nie verworfen/gemergt werden,
// sonst zerbricht das Transfer-Paar und Bestand verschwindet. Enges Fenster, damit ein
// späterer, unbeteiligter Versand gleicher Menge die echte MoonPay-Zusammenführung nicht
// fälschlich blockiert.
const TRANSFER_WINDOW_MS = 6 * 60 * 60 * 1000;
function isTransferReceive(tx, withdrawals) {
  const s = symOf(tx);
  const tA = txTimestampMs(tx);
  return withdrawals.some(w => {
    if (symOf(w) !== s || !amountsMatch(Number(w.menge), Number(tx.menge))) return false;
    const tB = txTimestampMs(w);
    if (Number.isNaN(tA) || Number.isNaN(tB)) return true; // ohne Zeit: konservativ schützen
    return Math.abs(tA - tB) <= TRANSFER_WINDOW_MS;
  });
}

export function findMoonPayLedgerPairs(allTxs) {
  const moonpayKaeufe = allTxs.filter(isMoonPayKauf);
  if (moonpayKaeufe.length === 0) return [];
  const withdrawals = allTxs.filter(t => t && t.typ === 'auszahlung' && Number(t.menge) > 0);

  const pairs = [];
  const moonpayUsed = new Set(); // 1:1 — ein MoonPay-Kauf matcht nur ein Ledger-TX
  for (const tx of allTxs) {
    if (!isLedgerIn(tx)) continue;
    if (isTransferReceive(tx, withdrawals)) continue; // Transfer-Empfang → nicht mergen
    const match = moonpayKaeufe.find(k => !moonpayUsed.has(k) && isMoonPayLedgerDuplicate(tx, k));
    if (match) {
      moonpayUsed.add(match);
      pairs.push({ ledger: tx, moonpay: match });
    }
  }
  return pairs;
}

/**
 * Findet On-Chain-Wallet-Eingänge, die das OUTPUT eines Wallet-Swaps doppeln.
 *
 * Beispiel: Ein SOL→ONDO-Swap im Ledger erzeugt 43,27 ONDO. Importiert man danach
 * die ETH-Adresse, taucht derselbe ONDO-Zufluss als On-Chain-`einzahlung` auf →
 * doppelter Bestand. Der Swap hält bereits Coin + Menge + Kostenbasis, also ist der
 * Wallet-Eingang redundant. Match: coinIn == Wallet-Coin, mengeIn ≈ Menge, Zeit ±30 min.
 *
 * @param {Array} allTxs  alle bekannten TX (existing + neue)
 * @returns {Array<{walletIn: object, swap: object}>}
 */
export function findSwapWalletInPairs(allTxs) {
  const swaps = allTxs.filter(t => t && t.typ === 'swap' && Number(t.mengeIn) > 0);
  if (swaps.length === 0) return [];
  // ⚠️ TRANSFER-SCHUTZ (Regression-Fix FLR 2025-12-19): Ein Wallet-Eingang mit einer
  // passenden Auszahlung derselben Coin+Menge ist ein TRANSFER (Coin in ein anderes
  // Wallet bewegt, z.B. Coinbase-Swap → Abhebung → Ledger-Eingang) — KEINE doppelte
  // Swap-Lieferung. Diese dürfen NIE verworfen werden, sonst zerbricht das Transfer-Paar
  // und der Bestand verschwindet. Nur ein Wallet-Eingang OHNE Gegen-Auszahlung ist eine
  // echte same-wallet Swap-Lieferung (z.B. Ledger-DEX-Swap, on-chain re-gesichtet).
  const withdrawals = allTxs.filter(t => t && t.typ === 'auszahlung' && Number(t.menge) > 0);
  const pairs = [];
  const swapUsed = new Set(); // 1:1 — ein Swap-Output matcht nur einen Wallet-Eingang
  for (const tx of allTxs) {
    if (!isWalletIn(tx)) continue;
    if (isTransferReceive(tx, withdrawals)) continue; // Transfer, kein Swap-Settlement → schützen
    const sym = symOf(tx);
    const tA = txTimestampMs(tx);
    if (Number.isNaN(tA)) continue;
    const match = swaps.find(s => {
      if (swapUsed.has(s)) return false;
      if (String(s.coinIn || s.symIn || '').toUpperCase() !== sym) return false;
      if (!amountsMatch(Number(tx.menge), Number(s.mengeIn))) return false;
      const tB = txTimestampMs(s);
      return !Number.isNaN(tB) && Math.abs(tA - tB) <= TIME_WINDOW_MS;
    });
    if (match) { swapUsed.add(match); pairs.push({ walletIn: tx, swap: match }); }
  }
  return pairs;
}

/**
 * Extrahiert die Wallet-Lieferdaten aus einem Ledger-TX, die in den MoonPay-Kauf
 * eingebettet werden. So bleibt die On-Chain-Spur (Hash, ggf. Adresse) auch nach
 * dem Löschen des Ledger-Eintrags am Kauf erhalten — für Beleg-Trail (§ 23 EStG)
 * und UI-Anzeige ("MoonPay → Ledger Wallet").
 */
export function buildWalletEnrichment(walletTx) {
  // exchange-Label aus dem Wallet-TX uebernehmen (z.B. "Phantom" statt hardcoded "Ledger")
  const label = walletTx?.exchange ? String(walletTx.exchange) : 'Ledger';
  const out = { wallet: label };
  if (walletTx?.hash) out.walletHash = walletTx.hash;
  return out;
}

/**
 * Findet redundante Ledger-`einzahlung`-Einträge in `candidateTxs`, die durch
 * einen MoonPay-`kauf` in `referenceTxs` ∪ `candidateTxs` abgedeckt sind.
 *
 * Verwendet `findMoonPayLedgerPairs` intern, gibt aber nur die Ledger-Seite zurück,
 * begrenzt auf Einträge in `candidateTxs` (Review-Screen-Kompatibilität).
 *
 * @param {Array} candidateTxs  TXs, die auf Redundanz geprüft werden
 * @param {Array} referenceTxs  Bestand zum Abgleichen (kann leer sein)
 * @returns {Map<any, object>}  Map von candidate-ID (`_editId` oder `id`) →
 *                              passender MoonPay-Kauf-TX
 */
export function findMoonPayLedgerDuplicates(candidateTxs, referenceTxs = []) {
  const pairs = findMoonPayLedgerPairs([...referenceTxs, ...candidateTxs]);
  if (pairs.length === 0) return new Map();

  const candidateSet = new Set(candidateTxs);
  const duplicates = new Map();
  for (const { ledger, moonpay } of pairs) {
    if (!candidateSet.has(ledger)) continue;
    const key = ledger._editId !== undefined
      ? ledger._editId
      : (ledger.id !== undefined ? ledger.id : ledger);
    duplicates.set(key, moonpay);
  }
  return duplicates;
}

/**
 * Filter-Variante: liefert `txs` ohne die als MoonPay-Duplikat erkannten Ledger-INs.
 * Praktisch für Stellen, an denen direkt eine bereinigte Liste gebraucht wird
 * (z.B. Tests, Batch-Import ohne Review-UI).
 */
export function dropMoonPayLedgerDuplicates(txs) {
  const pairs = findMoonPayLedgerPairs(txs);
  if (pairs.length === 0) return txs;
  const toDrop = new Set(pairs.map(p => p.ledger));
  return txs.filter(tx => !toDrop.has(tx));
}
